import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react';
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  AUTH_ACTION_PATH,
  ParsedFirebaseAuthAction,
  parseFirebaseAuthAction,
} from '../utils/firebaseAuthActions';
import './EmailVerificationActionPage.css';

type ActionStatus = 'processing' | 'verifySuccess' | 'resetReady' | 'resetSubmitting' | 'resetSuccess' | 'error';

const passwordLengthError = 'Password must be at least 6 characters';
const passwordMismatchError = 'Passwords do not match';

function getProcessingCopy(action: ParsedFirebaseAuthAction): { title: string; body: string } {
  if (action.mode === 'resetPassword') {
    return {
      title: 'Checking reset link',
      body: 'Please wait while we confirm this password reset link.',
    };
  }
  return {
    title: 'Verifying your email...',
    body: 'Please wait while we confirm your email address.',
  };
}

function getErrorTitle(action: ParsedFirebaseAuthAction): string {
  if (action.mode === 'resetPassword') {
    return 'Reset link unavailable';
  }
  if (action.mode === 'verifyEmail') {
    return 'Verification failed';
  }
  return 'Account link unavailable';
}

function getPasswordResetConfirmationError(err: unknown): string {
  const errorCode = typeof err === 'object' && err !== null && 'code' in err
    ? String((err as { code?: unknown }).code || '')
    : '';

  switch (errorCode) {
    case 'auth/weak-password':
      return 'Password is too weak. Please choose a stronger password.';
    case 'auth/expired-action-code':
    case 'auth/invalid-action-code':
    case 'auth/user-disabled':
    case 'auth/user-not-found':
      return 'This password reset link is invalid or expired. Please request a new reset email.';
    default:
      return 'Unable to update your password right now. Please try again.';
  }
}

const EmailVerificationActionPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, sendVerificationEmail, refreshVerificationStatus } = useAuth();
  const [status, setStatus] = useState<ActionStatus>('processing');
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const handledActionKeyRef = useRef<string | null>(null);

  const action = useMemo(
    () => parseFirebaseAuthAction(location.search, location.pathname),
    [location.pathname, location.search],
  );
  const processingCopy = getProcessingCopy(action);
  const isVerifyError = status === 'error' && action.mode === 'verifyEmail';

  useEffect(() => {
    if (status === 'verifySuccess' || status === 'resetSuccess') return;
    if (!action.active) return;

    const actionKey = `${action.mode}:${action.oobCode ?? ''}`;
    if (handledActionKeyRef.current === actionKey) return;
    handledActionKeyRef.current = actionKey;

    if (!action.oobCode) {
      setStatus('error');
      setError('This account action link is missing required information. Please request a new link.');
      return;
    }

    const oobCode = action.oobCode;

    let cancelled = false;
    (async () => {
      try {
        setStatus('processing');
        setError(null);

        if (action.mode === 'verifyEmail') {
          await applyActionCode(auth, oobCode);

          // Refresh user state/claims (if signed in) so backend sees email_verified=true.
          if (auth.currentUser) {
            await refreshVerificationStatus();
          }

          if (cancelled) return;
          setStatus('verifySuccess');
          navigate({ pathname: location.pathname || '/', search: '' }, { replace: true });
          return;
        }

        if (action.mode === 'resetPassword') {
          await verifyPasswordResetCode(auth, oobCode);

          if (cancelled) return;
          setStatus('resetReady');
          return;
        }

        setStatus('error');
        setError('This account action is not supported by this page. Please request a new link.');
      } catch (err: any) {
        console.error('Firebase auth action failed:', err);
        if (cancelled) return;
        setStatus('error');
        setError(
          action.mode === 'resetPassword'
            ? 'This password reset link is invalid or expired. Please request a new reset email.'
            : 'This verification link is invalid or expired. Please request a new one.'
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [action.active, action.mode, action.oobCode, location.pathname, navigate, refreshVerificationStatus, status]);

  const handleResend = async () => {
    setError(null);
    try {
      setResending(true);
      await sendVerificationEmail();
    } catch (err) {
      console.error('Failed to resend verification email:', err);
      setError('Failed to resend verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleResetPassword = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!action.oobCode) {
      setStatus('error');
      setError('This password reset link is missing required information. Please request a new reset email.');
      return;
    }
    if (newPassword.length < 6) {
      setError(passwordLengthError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(passwordMismatchError);
      return;
    }

    try {
      setError(null);
      setStatus('resetSubmitting');
      await confirmPasswordReset(auth, action.oobCode, newPassword);
      setNewPassword('');
      setConfirmPassword('');
      setStatus('resetSuccess');
      navigate({ pathname: location.pathname || AUTH_ACTION_PATH, search: '' }, { replace: true });
    } catch (err) {
      console.error('Password reset confirmation failed:', err);
      setStatus('resetReady');
      setError(getPasswordResetConfirmationError(err));
    }
  };

  const handleBackToApp = () => {
    navigate({ pathname: '/', search: '' }, { replace: true });
  };

  return (
    <div className="verify-action-page">
      <div className="verify-action-container">
        {status === 'processing' && (
          <>
            <div className="verify-action-header">
              <RefreshCw size={26} className="spin" />
              <h1>{processingCopy.title}</h1>
              <p>{processingCopy.body}</p>
            </div>
          </>
        )}

        {status === 'verifySuccess' && (
          <>
            <div className="verify-action-header success">
              <CheckCircle2 size={30} />
              <h1>Email verified</h1>
              <p>Your email has been verified. You can continue to the app.</p>
            </div>
            <button className="verify-action-primary" onClick={handleBackToApp}>
              Continue
            </button>
          </>
        )}

        {status === 'resetReady' && (
          <>
            <div className="verify-action-header">
              <h1>Set a new password</h1>
              <p>Choose a new password for your account.</p>
            </div>

            <form className="verify-action-form" onSubmit={handleResetPassword}>
              <div className="verify-action-field">
                <label htmlFor="newPassword">New password</label>
                <input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  minLength={6}
                  autoComplete="new-password"
                  required
                />
              </div>

              <div className="verify-action-field">
                <label htmlFor="confirmNewPassword">Confirm new password</label>
                <input
                  id="confirmNewPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  minLength={6}
                  autoComplete="new-password"
                  required
                />
              </div>

              {error && (
                <div className="verify-action-form-error" role="alert" aria-live="polite">
                  {error}
                </div>
              )}

              <button className="verify-action-primary" type="submit">
                Update password
              </button>
            </form>
          </>
        )}

        {status === 'resetSubmitting' && (
          <>
            <div className="verify-action-header">
              <RefreshCw size={26} className="spin" />
              <h1>Updating password</h1>
              <p>Please wait while we secure your account.</p>
            </div>
          </>
        )}

        {status === 'resetSuccess' && (
          <>
            <div className="verify-action-header success">
              <CheckCircle2 size={30} />
              <h1>Password updated</h1>
              <p>Your password has been updated. You can sign in with the new password.</p>
            </div>
            <button className="verify-action-primary" onClick={handleBackToApp}>
              Continue to sign in
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="verify-action-header error">
              <AlertTriangle size={30} />
              <h1>{getErrorTitle(action)}</h1>
              <p>{error ?? 'We could not complete this account action.'}</p>
            </div>

            <div className="verify-action-actions">
              {isVerifyError && currentUser && (
                <button className="verify-action-primary" onClick={handleResend} disabled={resending}>
                  {resending ? 'Sending…' : 'Resend verification email'}
                </button>
              )}
              <button className="verify-action-secondary" onClick={handleBackToApp}>
                <ArrowLeft size={16} />
                Back to sign in
              </button>
            </div>

            {isVerifyError && !currentUser && (
              <div className="verify-action-note">
                <p>Sign in to resend a verification email.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationActionPage;
